/**
 * The patient's care team. Which slots exist is per-clinic
 * (`care-team.slots`), so callers read `lead.care_team.slots` rather than
 * assuming a fixed pair.
 *
 * A different axis from `assignLead` in leads-api: that one routes the SALES
 * agent and drives row visibility. This one records who treats the patient,
 * changes nothing about who can see them, and is gated by `care_team.assign`
 * rather than `leads.assign`.
 *
 * The route sits behind `feature:care_team_enabled`, so it 404s on a clinic
 * that has not opted in — hide, don't disable.
 */

import api from "./api"
import type { ApiResponse, Lead } from "@/types"

/**
 * Slots to write. OMISSION AND NULL MEAN DIFFERENT THINGS: a key left out is
 * untouched, an explicit `null` clears the slot. That is what lets a caller
 * change the doctor without wiping the prosthetist, so always send exactly the
 * slot being edited.
 */
export interface CareTeamSlots {
  care_doctor_id?: number | null
  care_prosthetist_id?: number | null
  care_frontdesk_id?: number | null
}

/** The request field each slot writes to. */
export const CARE_TEAM_SLOT_FIELD = {
  doctor: "care_doctor_id",
  prosthetist: "care_prosthetist_id",
  frontdesk: "care_frontdesk_id",
} as const satisfies Record<string, keyof CareTeamSlots>

/** PUT /leads/{lead}/care-team — assign, reassign or clear either slot. */
export const updateCareTeam = async (
  leadId: string,
  slots: CareTeamSlots,
): Promise<Lead> => {
  const response = await api.put<ApiResponse<Lead>>(
    `/leads/${leadId}/care-team`,
    slots,
  )
  return response.data.data
}
